// Per-DEVICE UI scale for the POS.
//
// Unlike theme (which follows the user across stations via the DB), zoom is a
// property of the physical screen: a small tablet running the KDS wants the
// whole UI shrunk so more fits without horizontal scroll, while a 24" counter
// monitor wants it full size. So this is localStorage-only — never synced to
// the server, never tied to the user record.
//
// Applied by multiplying the ROOT FONT-SIZE via the `--ui-zoom` CSS variable
// (see the `html { font-size: calc(16px * var(--ui-zoom)) }` rule in
// index.css). Every rem-based Tailwind utility shrinks together, while
// viewport heights (dvh / 100% / vh) keep filling the screen and Radix popover
// positioning stays correct. We deliberately do NOT use CSS `zoom`: it shrank
// the `height:100%` document box and left white space below it on tablets, and
// it mis-placed dropdowns. We only ever SHRINK — capped at 1.0 (100%),
// flexible on the low end down to ZOOM_MIN.

const STORAGE_KEY = 'pos-ui-zoom'

export const ZOOM_MIN = 0.5
export const ZOOM_MAX = 1.0
export const ZOOM_STEP = 0.05

/** Snap to the nearest step and clamp into [ZOOM_MIN, ZOOM_MAX]. */
export function clampZoom(v: number): number {
  if (!Number.isFinite(v)) return ZOOM_MAX
  const snapped = Math.round(v / ZOOM_STEP) * ZOOM_STEP
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(snapped.toFixed(2))))
}

export function readZoom(): number {
  if (typeof window === 'undefined') return ZOOM_MAX
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return ZOOM_MAX
    const n = parseFloat(raw)
    return Number.isFinite(n) ? clampZoom(n) : ZOOM_MAX
  } catch {
    return ZOOM_MAX
  }
}

/** Apply to <html>. At 100% we remove the override entirely so nothing lingers. */
export function applyZoom(scale: number): void {
  if (typeof document === 'undefined') return
  const z = clampZoom(scale)
  const style = document.documentElement.style
  if (z === ZOOM_MAX) style.removeProperty('--ui-zoom')
  else style.setProperty('--ui-zoom', String(z))
}

/** Persist (per device) and apply immediately. Returns the clamped value used. */
export function setZoom(scale: number): number {
  const z = clampZoom(scale)
  try {
    window.localStorage.setItem(STORAGE_KEY, String(z))
  } catch {
    /* ignore — Safari private mode */
  }
  applyZoom(z)
  return z
}
